/**
 * En-tête façon Notion de l'espace apprenant (04/11/2026) : couverture pleine largeur, photo ronde du formateur qui
 * chevauche le bas de la couverture (comme l'icône d'une page Notion), nom du formateur, intitulé, message d'accueil.
 * Et l'éditeur du formateur : galerie, image téléversée, « Repositionner » (glisser ou curseur), message, aperçu en direct.
 */
import { useEffect, useId, useRef, useState } from "react";
import { Check, ImageUp, MoveVertical, RotateCcw, Trash2 } from "lucide-react";
import { COUVERTURES, couvertureGalerie } from "@/domaine/accueil-apprenant/couvertures";
import {
  MESSAGE_ACCUEIL_MAX,
  resoudreAccueil,
  type AccueilResolu,
  type CouvertureResolue,
  type NiveauAccueil,
} from "@/domaine/accueil-apprenant/reglages";
import { Avatar } from "./Avatar";
import { Bouton, cx } from "./base";

export function Couverture({
  couverture,
  className,
  repositionner,
}: {
  couverture: CouvertureResolue;
  className?: string;
  /** Mode « Repositionner » : glisser l'image verticalement (la position va de 0 = haut à 100 = bas). */
  repositionner?: (position: number) => void;
}) {
  const depart = useRef<{ y: number; position: number; hauteur: number } | null>(null);
  if (couverture.type === "galerie") {
    const g = couvertureGalerie(couverture.id);
    return (
      <div
        role="img"
        aria-label={`Couverture : ${g?.libelle ?? "dégradé"}`}
        className={cx("w-full", className)}
        style={{ background: couverture.fond }}
        data-couverture={couverture.id}
      />
    );
  }
  return (
    <div
      className={cx(
        "relative w-full overflow-hidden bg-papier-3",
        repositionner && "cursor-grab touch-none active:cursor-grabbing",
        className,
      )}
      onPointerDown={(e) => {
        if (!repositionner) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        depart.current = {
          y: e.clientY,
          position: couverture.position,
          hauteur: e.currentTarget.clientHeight || 1,
        };
      }}
      onPointerMove={(e) => {
        if (!repositionner || !depart.current) return;
        const d = depart.current;
        repositionner(
          Math.min(100, Math.max(0, Math.round(d.position - ((e.clientY - d.y) / d.hauteur) * 100))),
        );
      }}
      onPointerUp={() => (depart.current = null)}
      onPointerCancel={() => (depart.current = null)}
      data-couverture="image"
    >
      <img
        src={couverture.url}
        alt="Image de couverture choisie par le formateur"
        className="pointer-events-none size-full object-cover select-none"
        style={{ objectPosition: `50% ${couverture.position}%` }}
        draggable={false}
      />
      {repositionner && (
        <span className="absolute inset-x-0 top-1/2 mx-auto w-fit -translate-y-1/2 rounded-sm bg-encre/70 px-3 py-1.5 text-[13px] font-medium text-sur-accent">
          Glissez l'image pour la repositionner
        </span>
      )}
    </div>
  );
}

export interface FormateurAccueil {
  prenom: string;
  nom: string;
  photo: string | null;
}

/** L'en-tête vu par l'apprenant (et l'aperçu du formateur). `children` : ligne d'état du dossier, frise… */
export function BanniereAccueil({
  accueil,
  formateur,
  titre,
  children,
  repositionner,
  niveauTitre = 1,
}: {
  accueil: AccueilResolu;
  formateur: FormateurAccueil;
  titre: string;
  children?: React.ReactNode;
  repositionner?: (position: number) => void;
  niveauTitre?: 1 | 2;
}) {
  const nom = `${formateur.prenom} ${formateur.nom}`.trim() || "Votre formateur";
  const Titre = niveauTitre === 1 ? "h1" : "h2";
  return (
    <header
      className="overflow-hidden rounded-lg border border-trait bg-carte shadow-carte"
      data-testid="banniere-accueil"
    >
      <Couverture
        couverture={accueil.couverture}
        repositionner={repositionner}
        className="h-32 sm:h-44 lg:h-52"
      />
      <div className="relative px-4 pb-5 sm:px-7 sm:pb-6">
        <div className="-mt-10 flex items-end gap-3 sm:-mt-14">
          <Avatar
            nom={nom}
            photo={formateur.photo}
            taille="xl"
            decoratif={false}
            className="size-20! border-4 border-carte shadow-flottant sm:size-24!"
          />
          <p className="mb-1 min-w-0 text-[13px] leading-tight text-encre-2">
            <span className="block text-[11px] font-semibold tracking-wide text-encre-3 uppercase">
              Votre formateur
            </span>
            <span className="block truncate text-sm font-semibold text-encre">{nom}</span>
          </p>
        </div>
        <Titre className="mt-3 font-display text-[22px] leading-tight font-semibold text-balance sm:text-[28px]">
          {titre}
        </Titre>
        <blockquote
          className="mt-3 border-l-4 border-attente pl-3.5 text-[15px] leading-relaxed whitespace-pre-line text-encre-2"
          data-testid="message-accueil"
        >
          {accueil.message}
        </blockquote>
        {children}
      </div>
    </header>
  );
}

/**
 * Version compacte pour la liste « Mes formations » de l'apprenant (04/11/2026) : la même couverture en bandeau et la
 * photo du formateur qui la chevauche, en tête de la carte de chaque formation.
 */
export function CouvertureCarte({
  accueil,
  formateur,
}: {
  accueil: AccueilResolu | null;
  formateur: FormateurAccueil | null;
}) {
  const nom = formateur ? `${formateur.prenom} ${formateur.nom}`.trim() || "Votre formateur" : "";
  return (
    <div className="relative" data-testid="couverture-carte">
      {accueil ? (
        <Couverture couverture={accueil.couverture} className="h-20 sm:h-24" />
      ) : (
        <div className="h-20 w-full bg-papier-3 sm:h-24" aria-hidden />
      )}
      {formateur && (
        <Avatar
          nom={nom}
          photo={formateur.photo}
          taille="lg"
          decoratif
          className="absolute -bottom-6 left-4 size-12! border-[3px] border-carte shadow-flottant sm:left-5 sm:size-14!"
        />
      )}
    </div>
  );
}

export interface SaisieAccueilLocale {
  couverture: string;
  position: number;
  message: string;
}

/**
 * Éditeur de l'accueil. `niveau` : ce qui est enregistré à ce niveau ; `heritage` : le niveau dont on hérite (profil
 * du formateur pour un dossier ; rien pour le profil). L'aperçu se calcule ici, avec la même règle que le serveur.
 */
export function EditeurAccueil({
  niveau,
  heritage,
  formateur,
  titreApercu,
  enregistrer,
  deposer,
  retirerImage,
  reinitialiser,
  enCours,
  erreurs,
}: {
  niveau: NiveauAccueil | null;
  heritage: NiveauAccueil | null | undefined;
  formateur: FormateurAccueil;
  titreApercu: string;
  enregistrer: (s: SaisieAccueilLocale) => void;
  deposer: (f: File) => void;
  retirerImage: () => void;
  reinitialiser?: () => void;
  enCours: { enregistrer: boolean; deposer: boolean };
  erreurs?: Record<string, string>;
}) {
  const surcharge = heritage !== undefined;
  const depuis = (n: NiveauAccueil | null): SaisieAccueilLocale => ({
    couverture: n?.couverture ?? "",
    position: n?.position ?? 50,
    message: n?.message ?? "",
  });
  const [saisie, setSaisie] = useState<SaisieAccueilLocale>(() => depuis(niveau));
  const [repositionnement, setRepositionnement] = useState(false);
  // Les réglages relus du serveur (après un dépôt d'image, par exemple) remplacent la saisie.
  const cle = JSON.stringify(niveau);
  useEffect(() => {
    setSaisie(depuis(niveau));
  }, [cle]);

  const imageNiveau = niveau?.image ?? null;
  const local: NiveauAccueil = {
    couverture: saisie.couverture,
    position: saisie.position,
    message: saisie.message.trim() || null,
    image: imageNiveau,
  };
  const apercu = surcharge
    ? resoudreAccueil(heritage ?? null, local, formateur.prenom)
    : resoudreAccueil(local, null, formateur.prenom);
  const imageAffichee =
    apercu.couverture.type === "image" && (surcharge ? apercu.surcharge.couverture : true);
  const groupe = useId();
  const idMessage = useId();
  const idFichier = useId();
  const choisir = (couverture: string) => setSaisie((s) => ({ ...s, couverture }));
  const options: Array<{ valeur: string; libelle: string; fond?: string; image?: string | null }> = [
    ...(surcharge ? [{ valeur: "", libelle: "Comme mon profil" }] : []),
    ...(imageNiveau ? [{ valeur: "image", libelle: "Mon image", image: imageNiveau }] : []),
    ...COUVERTURES.map((c) => ({ valeur: `galerie:${c.id}`, libelle: c.libelle, fond: c.fond })),
  ];
  const selection = saisie.couverture === "" && !surcharge ? "galerie:sapin" : saisie.couverture;

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-[13px] font-semibold text-encre-2">Aperçu de ce que voit l'apprenant</p>
        <BanniereAccueil
          accueil={apercu}
          formateur={formateur}
          titre={titreApercu}
          niveauTitre={2}
          repositionner={
            repositionnement && imageAffichee
              ? (position) => setSaisie((s) => ({ ...s, position }))
              : undefined
          }
        />
      </div>

      <fieldset>
        <legend className="mb-2 text-sm font-semibold">Couverture</legend>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {options.map((o) => {
            const actif = selection === o.valeur;
            return (
              <label
                key={o.valeur || "heriter"}
                className={cx(
                  "group relative block cursor-pointer overflow-hidden rounded-md border-2 transition-colors duration-150 focus-within:ring-[3px] focus-within:ring-focus/60",
                  actif ? "border-accent" : "border-transparent hover:border-trait-fort",
                )}
              >
                <input
                  type="radio"
                  name={groupe}
                  value={o.valeur}
                  checked={actif}
                  onChange={() => choisir(o.valeur)}
                  className="sr-only"
                />
                <span
                  className="block h-12 w-full bg-papier-3 sm:h-14"
                  style={o.fond ? { background: o.fond } : undefined}
                  aria-hidden
                >
                  {o.image && <img src={o.image} alt="" className="size-full object-cover" />}
                  {o.valeur === "" && (
                    <span className="grid size-full place-items-center text-[11px] font-semibold text-encre-2">
                      Hériter
                    </span>
                  )}
                </span>
                <span className="block truncate bg-carte px-1.5 py-1 text-[11px] text-encre-2">
                  {o.libelle}
                </span>
                {actif && (
                  <span
                    className="absolute top-1 right-1 grid size-5 place-items-center rounded-full bg-accent text-sur-accent"
                    aria-hidden
                  >
                    <Check className="size-3.5" />
                  </span>
                )}
              </label>
            );
          })}
        </div>
        {erreurs?.couverture && <p className="mt-1.5 text-[13px] text-danger-encre">{erreurs.couverture}</p>}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            id={idFichier}
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            className="sr-only"
            tabIndex={-1}
            aria-label="Fichier de l'image de couverture"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) deposer(f);
              e.target.value = "";
            }}
          />
          <Bouton
            variante="secondaire"
            taille="sm"
            enCours={enCours.deposer}
            icone={<ImageUp className="size-4" aria-hidden />}
            onClick={() => document.getElementById(idFichier)?.click()}
          >
            {imageNiveau ? "Remplacer mon image" : "Téléverser une image"}
          </Bouton>
          {imageAffichee && (
            <Bouton
              variante={repositionnement ? "primaire" : "secondaire"}
              taille="sm"
              icone={
                repositionnement ? (
                  <Check className="size-4" aria-hidden />
                ) : (
                  <MoveVertical className="size-4" aria-hidden />
                )
              }
              onClick={() => setRepositionnement((r) => !r)}
              aria-pressed={repositionnement}
            >
              {repositionnement ? "Terminer" : "Repositionner"}
            </Bouton>
          )}
          {imageNiveau && (
            <Bouton
              variante="discret"
              taille="sm"
              icone={<Trash2 className="size-4" aria-hidden />}
              onClick={() => confirm("Retirer l'image de couverture ?") && retirerImage()}
            >
              Retirer l'image
            </Bouton>
          )}
        </div>
        {repositionnement && imageAffichee && (
          <label className="mt-3 flex items-center gap-3 text-[13px] text-encre-2">
            <span className="shrink-0">Position verticale</span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={saisie.position}
              onChange={(e) => setSaisie((s) => ({ ...s, position: Number(e.target.value) }))}
              className="w-full accent-(--color-accent)"
              aria-valuetext={`${saisie.position} % depuis le haut`}
            />
            <span className="chiffres w-10 shrink-0 text-right">{saisie.position} %</span>
          </label>
        )}
        <p className="mt-2 text-[12px] text-encre-3">
          JPG, PNG ou WebP, 8 Mo au plus, 400 px de large au moins. L'image est réduite et ré-enregistrée :
          ses métadonnées (lieu, appareil) sont supprimées.
        </p>
      </fieldset>

      <div>
        <label htmlFor={idMessage} className="mb-1.5 block text-sm font-semibold">
          Message d'accueil
        </label>
        <textarea
          id={idMessage}
          rows={4}
          maxLength={MESSAGE_ACCUEIL_MAX}
          value={saisie.message}
          onChange={(e) => setSaisie((s) => ({ ...s, message: e.target.value }))}
          placeholder={
            surcharge
              ? (heritage?.message ?? "Le message de votre profil s'affichera.")
              : "Bienvenue ! Je suis ravi(e) de vous accompagner…"
          }
          aria-describedby={`${idMessage}-aide`}
          aria-invalid={erreurs?.message ? true : undefined}
          className="w-full rounded-sm border border-trait-fort bg-carte px-3 py-2.5 text-[15px] leading-relaxed hover:border-encre-3 focus:border-accent focus:ring-[3px] focus:ring-accent/40 focus:outline-none"
        />
        <p id={`${idMessage}-aide`} className="mt-1 flex justify-between gap-3 text-[12px] text-encre-3">
          <span>
            {erreurs?.message ? (
              <span className="text-danger-encre">{erreurs.message}</span>
            ) : surcharge ? (
              "Laissez vide pour reprendre le message de votre profil."
            ) : (
              "Votre ton, votre état d'esprit : quelques lignes suffisent. Laissez vide pour le message par défaut."
            )}
          </span>
          <span className="chiffres shrink-0">
            {saisie.message.length}/{MESSAGE_ACCUEIL_MAX}
          </span>
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Bouton
          variante="primaire"
          enCours={enCours.enregistrer}
          onClick={() => (setRepositionnement(false), enregistrer(saisie))}
        >
          Enregistrer l'accueil
        </Bouton>
        {reinitialiser && (
          <Bouton
            variante="discret"
            icone={<RotateCcw className="size-4" aria-hidden />}
            onClick={() =>
              confirm("Revenir à l'accueil de votre profil pour ce dossier ?") && reinitialiser()
            }
          >
            Revenir à mon profil
          </Bouton>
        )}
      </div>
    </div>
  );
}
